import { isBackCommand } from "../../utils/command/commandLineUtils";
import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslationFunction } from "../../utils/core/translationUtils";
import { clearAllCommandState } from "../../utils/command/commandStateUtils";
import {
  parseMarkedText,
  stripMarkers,
} from "../../utils/ui/outputProcessingUtils";

/**
 * Writes a multi-line prompt to the store with typing effect, splitting any
 * leading lines into output and setting the final line as the active prompt.
 */
async function emitPromptWithTypingEffect(prompt) {
  const addOutputWithTypingEffect =
    useCommandFlowStore.getState().addOutputWithTypingEffect;
  const setCurrentPromptWithTypingEffect =
    useCommandFlowStore.getState().setCurrentPromptWithTypingEffect;

  if (typeof prompt === "string" && prompt.includes("\n")) {
    const lines = prompt.split("\n");
    const lastLine = lines.pop();
    if (lines.length > 0) {
      // Join into a single output entry so one Enter skips the whole block.
      // CSS `white-space: pre-wrap` renders the embedded \n as line breaks.
      const outputLines = [
        { type: "output", text: parseMarkedText(lines.join("\n")) },
      ];
      await addOutputWithTypingEffect(outputLines);
      // Keep the input area in a "busy" state so neither the default ">"
      // prompt nor any leftover prior prompt flashes between the end of
      // pre-prompt output typing and the start of prompt typing.
      useCommandFlowStore.setState({ isLoading: true });
    }
    await setCurrentPromptWithTypingEffect(stripMarkers(lastLine));
  } else {
    await setCurrentPromptWithTypingEffect(stripMarkers(prompt));
  }
}

/**
 * Handles "back" command input during a session.
 *
 * Behavior:
 * 1. If there is a previous prompted step inside the current command, move
 *    back to it (existing history-pop behavior).
 * 2. If at the first prompted step AND the redirect parent chain is
 *    non-empty, pop one parent and redirect to it. This lets users walk
 *    back through umbrella/sub-umbrella chains.
 * 3. If at the first prompted step AND the chain is empty, treat "back"
 *    as "exit": end the session and return to the main command line.
 */
export async function handleBackCommandWithSetters({
  trimmed,
  currentPrompt,
  setters,
  engine,
  onCommandBack,
  activeCommandName,
}) {
  if (!isBackCommand(trimmed)) return false;

  // Fire onCommandBack on EVERY back keystroke, regardless of which branch
  // executes below. Semantically, the user's intent ("go back") happens at
  // the moment they type `πίσω` — whether the session ultimately continues,
  // pops to a parent, or ends is a downstream side effect that doesn't
  // change what they did.
  if (onCommandBack && activeCommandName) {
    const sessionDataSnapshot =
      useCommandFlowStore.getState().sessionData ?? {};
    try {
      onCommandBack(activeCommandName, sessionDataSnapshot);
    } catch (err) {
      console.error("onCommandBack threw:", err);
    }
  }

  const setOutput = useCommandFlowStore.getState().setOutput;
  const currentOutput = useCommandFlowStore.getState().output;
  const addOutputWithTypingEffect =
    useCommandFlowStore.getState().addOutputWithTypingEffect;

  // Echo the user's input line to output (prompt + "back")
  const promptWithInputLine = {
    type: "output",
    text: [
      { text: currentPrompt ? currentPrompt.trimEnd() : "", color: "output" },
      { text: " ", color: null },
      { text: trimmed, color: "input" },
    ],
  };

  // Clear the live prompt and mark the input area as busy *atomically* so
  // the prior prompt does not stay visible (or flash on/off) during the
  // async processCurrentStep + pre-prompt output typing that follows.
  useCommandFlowStore.setState({ currentPrompt: null, isLoading: true });

  if (engine.sessionManager.isAtFirstPromptedStep()) {
    setOutput([...currentOutput, promptWithInputLine]);

    const chain = Array.isArray(engine.redirectParentChain)
      ? engine.redirectParentChain
      : [];

    if (chain.length > 0) {
      // Pop one level and re-enter the parent command.
      const parent = chain.pop();
      try {
        engine.endSession();
      } catch (_) {
        /* already ended */
      }

      const result = await engine.startCommand(
        parent,
        {},
        { isRedirect: true }
      );

      if (result) {
        if (result.output) {
          const outputArr = Array.isArray(result.output)
            ? result.output.map((line) =>
                typeof line === "string"
                  ? { type: "output", text: parseMarkedText(line) }
                  : line
              )
            : [{ type: "output", text: parseMarkedText(result.output) }];
          await addOutputWithTypingEffect(outputArr);
        }
        if (result.prompt) {
          await emitPromptWithTypingEffect(result.prompt);
        }
      }
      return true;
    }

    // Chain empty → behave like exit/cancel.
    const t = getTranslationFunction();
    const cancelMessage = t("commandCancelled");
    await addOutputWithTypingEffect([
      { type: "output", text: cancelMessage },
    ]);

    // If frame mode was active, end it and clear the terminal.
    const { frameMode, endFrameMode, setWasCleared } =
      useCommandFlowStore.getState();
    if (frameMode) {
      endFrameMode();
      setOutput([]);
      setWasCleared(true);
      setTimeout(
        () => useCommandFlowStore.getState().setWasCleared(false),
        100
      );
    }

    clearAllCommandState();
    setters.setCurrentPrompt(null);
    try {
      engine.sessionManager.endSession();
    } catch (_) {
      /* already ended */
    }
    engine.redirectParentChain = [];

    return true;
  }

  // Not at first prompted step → pop history and re-emit the previous prompt.
  setOutput([...currentOutput, promptWithInputLine]);
  engine.sessionManager.moveToPreviousPromptedStep();

  const result = await engine.stepFlowManager.processCurrentStep("");
  if (result && result.prompt) {
    await emitPromptWithTypingEffect(result.prompt);
  }

  return true;
}
